// src/pages/admin/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { Users, Package, CreditCard, TrendingUp } from 'lucide-react';
import AdminHeader from '../../components/admin/common/AdminHeader';
import StatCard from '../../components/admin/dashboard/StatCard';
import OrdersChart from '../../components/admin/dashboard/OrdersChart';
import RevenueChart from '../../components/admin/dashboard/RevenueChart';
import StockAlerts from '../../components/admin/dashboard/StockAlerts';
import { adminStatsService, type DashboardStats } from '../../services/adminStatsService';
import { formatPrice } from '../../utils/helpers';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const dashboardStats = await adminStatsService.getDashboardStats();
        setStats(dashboardStats);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <div key={i} className="h-80 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatPrice(stats?.orderStats.totalRevenue || 0),
      change: stats?.growthStats.revenue || 0,
      icon: CreditCard
    },
    {
      title: 'Total Orders',
      value: stats?.orderStats.totalOrders.toString() || '0',
      change: stats?.growthStats.orders || 0,
      icon: Package
    },
    {
      title: 'Average Order Value',
      value: formatPrice(stats?.orderStats.averageOrderValue || 0),
      change: 0, // Calculate this if needed
      icon: TrendingUp
    },
    {
      title: 'Low Stock Items',
      value: stats?.productStats.lowStockProducts.length.toString() || '0',
      change: 0, // Not applicable for this metric
      icon: Package
    }
  ];

  return (
    <div className="space-y-6">
      <AdminHeader title="Dashboard" onMenuClick={() => {}} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 px-6">
        {statCards.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            icon={stat.icon}
          />
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-6">
        {stats && (
          <>
            <RevenueChart data={stats.orderStats.monthlyOrders} />
            <OrdersChart data={stats.orderStats.monthlyOrders} />
          </>
        )}
      </div>

      {/* Low Stock Alerts */}
      <div className="px-6">
        {stats && <StockAlerts products={stats.productStats.lowStockProducts} />}
      </div>

      {/* Recent Orders */}
      <div className="px-6 pb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Recent Orders</h2>
          <div className="space-y-4">
            {stats?.recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium">Order #{order.id.slice(-6)}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(order.createdAt.toDate()).toLocaleString()}
                  </p>
                </div>
                <span className="px-3 py-1 text-sm bg-brand-blue-50 text-brand-blue-500 rounded-full">
                  {formatPrice(order.total)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;